import React, {
  FC,
  useState,
  useRef,
  MutableRefObject,
  useEffect
} from 'react';
import { Input, Tag as AntTag, AutoComplete, message } from 'antd';
import { Plus, Tag as TagIcon, Sparkles } from 'lucide-react';
import { Tag } from '../../../../models/Project';
import DetailsLabel from '../DetailsLabel';
import * as translations from './strings';
import { getLangKey } from '../../../../models/Language';

// @ts-ignore
import styles from './styles.module.css';
import cx from 'classnames';
import Loader from '../../../../components/Loader';
import track from '../../../../../../redesign/javascript/utils/analytics';

type Props = {
  onUpdateTags: (tagIds: (number | null)[]) => void;
  onCreateTag: (newTagName: string) => void;
  onRemoveTag: (id: number | null) => void;
  tags: Tag[];
  tagSources: (number | null)[];
  tagIds: (number | null)[];
  allTags: Tag[];
  canEditTags: boolean;
  canCreateTag: boolean;
};

export enum TagSource {
  User = 0,
  AI = 1
}

const strings = translations[getLangKey()];

const Tags: FC<Props> = ({
  onUpdateTags,
  onCreateTag,
  onRemoveTag,
  tags,
  tagSources,
  canEditTags,
  allTags,
  tagIds,
  canCreateTag
}) => {
  const [showInput, setShowInput] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [newTag, setNewTag] = useState<string>('');
  const inputRef: MutableRefObject<null | Input> = useRef(null);

  const getTag = (_tags: Tag[], _value: string): Tag | undefined =>
    _tags.find(({ name }: Tag) => name === _value);

  const hasId = (id: number | null): boolean => tagIds.includes(id);

  useEffect(() => {
    if (!newTag) return;
    const tag = getTag(tags, newTag);
    if (tag && tag.id && tag.id > 0) setNewTag('');
  }, [allTags, tags]);

  const getOptions = () =>
    allTags
      .filter(({ name, id, active }: Tag) => {
        const _name = name.toLowerCase();
        const _value = value.toLowerCase();
        return !_value
          ? active && !hasId(id)
          : (active && _name.slice(0, _value.length) === _value) ||
              _name.includes(_value);
      })
      .map((tag: Tag) => ({
        value: tag.name,
        label: (
          <div key={tag.id} className={styles.optionTag}>
            <TagIcon className={styles.tagIcon} />
            <span className={styles.tagName}>{tag.name}</span>
          </div>
        )
      }));

  const getTagIcon = (tag: Tag, source: TagSource | null) => {
    if (tag.id === -1) {
      return <Loader useDarkStyles className={styles.loaderIcon} />;
    }
    if (source === TagSource.AI) {
      return <Sparkles className={styles.tagIcon} />;
    }
    return <TagIcon className={styles.tagIcon} />;
  };

  const getTags = () => {
    const tagsClone = tags.slice();
    if (newTag && !getTag(tags, newTag)) {
      tagsClone.push({
        id: -1,
        name: newTag,
        active: true
      });
    }
    return tagIds.length
      ? tagsClone.map((tag: Tag, idx: number) => {
          const source = tagSources?.[idx];
          const tagClass =
            source === TagSource.AI
              ? cx(styles.tag, styles.aiSuggestion)
              : styles.tag;
          return (
            <span className={styles.tagHolder} key={tag.id}>
              <AntTag
                className={tagClass}
                closable={tag.id === -1 ? false : canEditTags}
                onClose={() => {
                  console.log('source', source);
                  if (source === TagSource.AI) {
                    track('Ai Tag Removed', { removed: tag.name }, {});
                  }
                  onRemoveTag(tag.id);
                }}
              >
                <span className={styles.tagContent} title={tag.name}>
                  {getTagIcon(tag, source)}
                  <span className={styles.innerTag}>{tag.name}</span>
                </span>
              </AntTag>
            </span>
          );
        })
      : null;
  };

  const blurInput = () => {
    setShowInput(false);
    setValue('');
  };

  const handleOnBlur = async () => {
    if (value && !getTag(tags, value)) {
      if (canCreateTag) {
        onCreateTag(value);
        setNewTag(value);
      } else {
        message.warning(strings.cantCreateTag);
      }
    }
    blurInput();
  };

  const handleOnKeyDown = (event: any) => {
    const escapeCode: number = 27;
    const enterCode: number = 13;
    if (event?.keyCode === escapeCode) {
      blurInput();
    } else if (event?.keyCode === enterCode) {
      handleOnBlur();
    }
  };

  const handleOnSelect = (_value: string) => {
    const updatedTags = tagIds.slice();
    const addedTag = getTag(allTags, _value);
    if (addedTag) {
      updatedTags.push(addedTag.id);
      onUpdateTags(updatedTags);
    }
    setValue('');
  };

  const handleOnAdd = () => {
    if (newTag && !getTag(tags, newTag)) return;
    setShowInput(true);
    setTimeout(() => {
      setShowDropdown(true);
      inputRef?.current?.focus();
    }, 300);
  };

  return (
    <div className={styles.tagsOuter}>
      <DetailsLabel label={strings.tags} />
      <div className={styles.tagsInner}>
        <div className={styles.tagsBox}>
          <div className={styles.tagsWrapper}>{getTags()}</div>
          {canEditTags && (
            <div className={styles.inputBox}>
              {showInput ? (
                <AutoComplete
                  className={styles.tagsAutoComplete}
                  getPopupContainer={trigger => trigger.parentElement}
                  options={getOptions()}
                  value={value}
                  onChange={setValue}
                  onBlur={() => handleOnBlur()}
                  dropdownMatchSelectWidth
                  onSelect={handleOnSelect}
                  onDropdownVisibleChange={setShowDropdown}
                  open={showDropdown}
                >
                  <Input
                    ref={inputRef}
                    className={styles.tagsInput}
                    type="text"
                    onKeyDown={handleOnKeyDown}
                    placeholder={strings.newTag}
                  />
                </AutoComplete>
              ) : (
                <AntTag
                  onClick={handleOnAdd}
                  className={cx(styles.tag, styles.addTag)}
                >
                  <Plus className={styles.addTagIcon} /> {strings.newTag}
                </AntTag>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tags;
